import React from 'react';
import { Button, FormControl, Grid, MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { AccountData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useAreasView } from './AreasViewContext';
import { formatSeconds } from './utils/areasFormat';
import { accountSchedulerConfigs, slotIndexOf, slotStartSeconds } from './utils/occupancyMath';
import { configDayBounds } from './utils/workingHours';

const DEFAULT_SLOT_MINUTES = 30;

/**
 * "עכשיו" replaces "היום" in this view: it both returns to today and re-enters live mode. Picking a
 * time from the dropdown pins it, and nothing moves it back until the admin says so.
 */
export const AreasToolbarControls = ({ onGoToToday }: { onGoToToday: () => void }) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { currentDate } = useCalendar();
  const { selectedSeconds, isLive, pinTime, enterLiveMode, requestScrollTo, nowSeconds, accountToday } = useAreasView();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const timeFormat = getTimeFormat(account);
  // On a phone the controls share one row with the menu, view select and dates, so they stay compact.
  const controlHeight = '44px';
  const configs = accountSchedulerConfigs(account);
  const slotMinutes = configs[0]?.slot_minutes || DEFAULT_SLOT_MINUTES;

  const bounds = configs
    .map((config) => configDayBounds(config, account, currentDate))
    .filter((value): value is { startSeconds: number; endSeconds: number } => Boolean(value));

  const startSlot = bounds.length > 0 ? slotIndexOf(Math.min(...bounds.map((b) => b.startSeconds)), slotMinutes) : 0;
  const endSlot =
    bounds.length > 0
      ? slotIndexOf(Math.max(...bounds.map((b) => b.endSeconds)) - 1, slotMinutes)
      : Math.floor((24 * 60) / slotMinutes) - 1;

  const options: number[] = [];
  for (let slot = startSlot; slot <= endSlot; slot++) {
    options.push(slotStartSeconds(slot, slotMinutes));
  }

  const selectedOption = slotStartSeconds(slotIndexOf(selectedSeconds, slotMinutes), slotMinutes);
  // Live mode can sit outside the configured hours — before opening, or late at night. Keep that
  // slot selectable rather than rendering a blank picker that disagrees with the pillars.
  if (options.length > 0 && !options.includes(selectedOption)) {
    const insertAt = options.findIndex((seconds) => seconds > selectedOption);
    options.splice(insertAt === -1 ? options.length : insertAt, 0, selectedOption);
  }

  const isViewingToday = currentDate === accountToday;
  const isFollowingClock = isLive && isViewingToday;

  return (
    <Grid container gap={isSmallScreen ? 0.5 : 1} alignItems="center" wrap="nowrap" sx={{ width: 'auto' }}>
      <Button
        variant={isFollowingClock ? 'soft' : 'outlined'}
        color={isFollowingClock ? 'primary' : 'inherit'}
        aria-live="polite"
        sx={{
          height: controlHeight,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          ...(isSmallScreen && { paddingInline: 1 }),
          // Following the clock is a live state, not just a selected button: a pulsing dot in the
          // soft green button reads as "this screen is moving" at a glance.
          ...(isFollowingClock && {
            '@keyframes areasLivePulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.35, transform: 'scale(0.75)' },
            },
          }),
        }}
        onClick={() => {
          if (!isViewingToday) {
            onGoToToday();
          }
          enterLiveMode();
          requestScrollTo(nowSeconds, true);
        }}>
        <Grid container gap={0.75} alignItems="center" wrap="nowrap">
          {isFollowingClock && !isSmallScreen && (
            <FiberManualRecordIcon
              sx={{ fontSize: '12px', color: 'primary.main', animation: 'areasLivePulse 1.6s ease-in-out infinite' }}
            />
          )}
          {t('calendar.areas_view.now')}
        </Grid>
      </Button>

      <FormControl sx={{ flexShrink: 0, width: isSmallScreen ? '76px' : 'auto' }}>
        <Select
          size="small"
          sx={{
            minHeight: controlHeight,
            fontVariantNumeric: 'tabular-nums',
            ...(isSmallScreen && {
              fontWeight: 700,
              '& .MuiSelect-select': { paddingInlineStart: '10px', paddingInlineEnd: '24px !important' },
              '& .MuiSelect-icon': { insetInlineEnd: 0 },
            }),
          }}
          value={options.length > 0 ? selectedOption : ''}
          onChange={(event) => {
            const seconds = Number(event.target.value);
            pinTime(seconds);
            requestScrollTo(seconds);
          }}
          inputProps={{ 'aria-label': t('calendar.areas_view.pick_time'), dir: 'ltr' }}>
          {options.map((seconds) => (
            <MenuItem key={seconds} value={seconds} dir="ltr">
              {formatSeconds(seconds, timeFormat)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
};
